import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';

import { IconButton } from '@atlaskit/button/new';
import FileIcon from '@atlaskit/icon/glyph/file';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import { StickyDiffHeader } from 'src/components/diff-set/diff-set.style';
import { useUntruncatedDiffCounts } from 'src/components/diff-set/hooks/untruncated-diff-counts';
import isMobile from 'src/components/sidebar/src/is-mobile';
import { useIntl } from 'src/hooks/intl';

import { DiffCommentNavigator } from '../../diff-comment-navigator/diff-comment-navigator';
import { DiffFeaturesContext } from '../../diff-features-context';
import { DiffSort } from '../../diff-sort';
import { IterativeReviewSelector } from '../../iterative-review/iterative-review-selector';
import { ReviewedFilesProgressBar } from '../../reviewed-files-progress-bar/reviewed-files-progress-bar';
import { SingleFileNavigation } from '../../single-file-mode/single-file-navigation';

import { DiffSettingsButton } from './diff-tab-settings-button';
import { messages } from './diff-tab.i18n';
import { FileTreeContext } from './file-tree-context';

type DiffTabHeaderProps = {
  isSingleFileModeActive: boolean;
  isStickyHeader?: boolean;
  setTabbedDiffHeaderHeight?: Dispatch<SetStateAction<number | undefined>>;
};

export const DiffTabHeader = ({
  isSingleFileModeActive,
  isStickyHeader = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTabbedDiffHeaderHeight = () => {},
}: DiffTabHeaderProps): JSX.Element => {
  const diffHeaderRef = useRef<HTMLDivElement>(null);
  const [isCommentNavOpen, setIsCommentNavOpen] = useState(false);
  const [isDiffSortOpen, setIsDiffSortOpen] = useState(false);
  const intl = useIntl();
  const [isIterativeReviewSelectorOpen, setIsIterativeReviewSelectorOpen] =
    useState(false);
  const { isSidebarCollapsed, setIsSidebarCollapsed } =
    useContext(FileTreeContext);
  const tabIndex = isStickyHeader ? -1 : 0;

  const { hasMarkFilesAsViewed } = useContext(DiffFeaturesContext);

  const isMobileHeaderActive = isMobile();

  const { untruncatedDiffFileCount } = useUntruncatedDiffCounts();

  useEffect(() => {
    const heightObserver = new ResizeObserver(entries => {
      entries.forEach(entry =>
        setTabbedDiffHeaderHeight(entry.contentRect.height)
      );
    });

    if (diffHeaderRef.current) {
      heightObserver.observe(diffHeaderRef.current);
    }

    return () => {
      heightObserver.disconnect();
    };
  }, [setTabbedDiffHeaderHeight, isMobileHeaderActive]);

  const diffHeaderStyles = xcss({
    marginInlineStart: isStickyHeader ? 'space.negative.050' : undefined,
    marginInlineEnd: isStickyHeader ? 'space.100' : undefined,
  });

  const buttonAppearance = isStickyHeader ? 'subtle' : 'default';

  const diffHeaderContent = (
    <Inline
      spread="space-between"
      alignBlock="center"
      shouldWrap
      space="space.050"
      xcss={diffHeaderStyles}
    >
      <Inline space="space.050" shouldWrap>
        {isMobileHeaderActive && (
          <Tooltip content={intl.formatMessage(messages.toggleFileView)}>
            <IconButton
              icon={FileIcon}
              label={intl.formatMessage(messages.toggleFileView)}
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              tabIndex={tabIndex}
            />
          </Tooltip>
        )}
        <IterativeReviewSelector
          isIterativeReviewSelectorOpen={isIterativeReviewSelectorOpen}
          setIsIterativeReviewSelectorOpen={setIsIterativeReviewSelectorOpen}
          isStickyHeader={isStickyHeader}
        />
        <DiffCommentNavigator
          isCommentNavOpen={isCommentNavOpen}
          setIsCommentNavOpen={setIsCommentNavOpen}
          isStickyHeader={isStickyHeader}
        />
        <DiffSort
          isDiffSortOpen={isDiffSortOpen}
          setIsDiffSortOpen={setIsDiffSortOpen}
          isStickyHeader={isStickyHeader}
        />
      </Inline>
      <Inline alignBlock="center" space="space.100">
        {hasMarkFilesAsViewed && (
          <ReviewedFilesProgressBar
            tabIndex={tabIndex}
            numTotalFiles={untruncatedDiffFileCount}
            hideProgressBar={isStickyHeader}
          />
        )}
        {isSingleFileModeActive && (
          <SingleFileNavigation
            tabIndex={tabIndex}
            appearance={buttonAppearance}
          />
        )}
        <DiffSettingsButton tabIndex={tabIndex} appearance={buttonAppearance} />
      </Inline>
    </Inline>
  );

  return isMobileHeaderActive ? (
    <StickyDiffHeader
      ref={diffHeaderRef}
      isCommentNavOpen={isCommentNavOpen}
      isDiffSortOpen={isDiffSortOpen}
      isIterativeReviewSelectorOpen={isIterativeReviewSelectorOpen}
    >
      {diffHeaderContent}
    </StickyDiffHeader>
  ) : (
    <Box paddingBlockEnd={isStickyHeader ? 'space.0' : 'space.150'}>
      {diffHeaderContent}
    </Box>
  );
};
